import AngleLeft from "@icons/angle-left.svg?react";
import { Trans } from "@lingui/react/macro";
import { useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { ErrorBoundary } from "react-error-boundary";
import invariant from "tiny-invariant";

import { useGuard } from "@/common/acl/action-guard/action-guard";
import { Button } from "@/common/components/button/button";
import {
  CompanyForm as CompanyFormComponent,
  Fields,
  getInitialValues,
} from "@/common/components/company-form/company-form";
import { Failed } from "@/common/components/info/info";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCompany } from "@/common/providers/company-provider";
import { useSidebar } from "@/common/providers/sidebar-provider/sidebar-provider";
import { splitCallingCodeAndCountry } from "@/common/utils/phone";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";

import { companyQuery, useUpdateCompanyMutation } from "./company-form.api";

const CompanyForm = () => {
  const sidebar = useSidebar();

  return (
    <FullscreenModalLayout
      headerButton={
        <button onClick={sidebar.actions.executeCloseAction}>
          <AngleLeft />
        </button>
      }
    >
      <ErrorBoundary fallbackRender={({ error }) => <Failed error={error} />}>
        <Form />
      </ErrorBoundary>
    </FullscreenModalLayout>
  );
};

const Form = () => {
  const queryClient = useQueryClient();
  const sidebar = useSidebar();
  const { company: activeCompany } = useCompany();
  invariant(activeCompany);
  const {
    data: { company, disabledFields },
  } = useSuspenseQuery(companyQuery({ companyId: activeCompany.id }));
  const updateCompany = useUpdateCompanyMutation();
  const initialValues = getInitialValues(company);
  const handleSubmit = (data: Fields) => {
    updateCompany.mutate(
      {
        address: data.address,
        companyForm: data.legalForm,
        contactEmail: data.contact.email,
        contactName: data.contact.name,
        contactPhone: {
          countryCode: splitCallingCodeAndCountry(
            data.contact.phone.countryCode,
          ).at(0)!,
          phoneNumber: data.contact.phone.phoneNumber,
        },
        contactPosition: data.contact.role,
        id: company.id,
        vat: data.vat,
      },
      {
        onError: () => {
          sidebar.actions.openScreen("user-failure");
        },
        onSuccess: async () => {
          await queryClient.invalidateQueries({
            queryKey: ["company-info"],
          });
          sidebar.actions.openScreen("user-success");
        },
      },
    );
  };
  const guardedSubmit = useGuard({ action: handleSubmit });

  return (
    <>
      <Spacer className="h-6" />
      <h1 className="text-2xl font-black text-can-forest-teal">
        <Trans>Informace o společnosti</Trans>
      </h1>
      <Spacer className="h-6" />
      <CompanyFormComponent
        disabledFields={disabledFields}
        initialValues={initialValues}
        onSubmit={guardedSubmit}
      />
      <Button.Container>
        <Button
          disabled={updateCompany.status === "pending"}
          form="company-form"
          type="submit"
          variant="primary"
        >
          <Trans>Uložit</Trans>
        </Button>
      </Button.Container>
    </>
  );
};

export { CompanyForm };
